import { createSelector } from '@reduxjs/toolkit';
import { getCurrentAccountId } from 'redux/selectors/accounts';
import type { IState } from 'redux/state';
import { getShouldAuthenticateToTic } from '~redux/selectors/user';
import { ticApi } from './api-slice/tic.apiSlice';

const readAuthState = (state: IState) => state.ticAuth;
const readTicApiSlice = (state: IState) => state[ticApi.reducerPath];

export const getIsTicAuthenticated = createSelector([readAuthState], (authState): boolean => {
  return authState.isAuthenticated;
});

/**
 * Returns true when it's safe to use the TIC API.
 */
export const getUseTicAPI = createSelector(
  [getShouldAuthenticateToTic, getIsTicAuthenticated],
  (enabled, authenticated) => enabled && authenticated
);

/**
 * Ref `tic.createApi.base.ts`
 */
export const getTicSessionsRTKQueryBase = createSelector([readTicApiSlice], localState => {
  return ticApi.endpoints.getTicSession.select(undefined)({
    [ticApi.reducerPath]: localState,
  });
});

/**
 * Returns TIC user's `personalSpace`, and the `workspace` that maps to the current Timely account.
 * Most useful when interacting with the TIC API which often needs to know the TIC account ID.
 */
export const getContextualTicAccounts = createSelector(
  [getTicSessionsRTKQueryBase, getCurrentAccountId],
  ({ data }, currentTimelyAccountId) => {
    return {
      user: data?.tic_user,
      personalSpace: data?.tic_accounts?.find(ticAccount => ticAccount.type === 'personal'),
      workspace: data?.tic_accounts?.find(
        ticAccount => ticAccount.timely_account_id === currentTimelyAccountId
      ),
    };
  }
);
